<template>
  <div class="login-bg min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
    <!-- Atmospheric background effects -->
    <div class="absolute inset-0 pointer-events-none">
      <!-- Stadium lights radial glow -->
      <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full opacity-20"
        style="background: radial-gradient(ellipse at center top, rgba(200, 155, 60, 0.6) 0%, transparent 70%)" />
      <!-- Left glow -->
      <div class="absolute bottom-1/4 left-0 w-72 h-72 rounded-full opacity-15"
        style="background: radial-gradient(circle, rgba(186, 12, 47, 0.5) 0%, transparent 70%)" />
      <!-- Right glow -->
      <div class="absolute top-1/3 right-0 w-72 h-72 rounded-full opacity-15"
        style="background: radial-gradient(circle, rgba(26, 68, 128, 0.8) 0%, transparent 70%)" />

      <!-- Baseball seam decorations -->
      <svg class="absolute top-8 right-8 w-24 h-24 opacity-[0.07]" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="46" stroke="white" stroke-width="2"/>
        <path d="M30 10 C15 28, 15 72, 30 90" stroke="#BA0C2F" stroke-width="3" stroke-linecap="round"/>
        <path d="M70 10 C85 28, 85 72, 70 90" stroke="#BA0C2F" stroke-width="3" stroke-linecap="round"/>
        <line x1="28" y1="16" x2="22" y2="20" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="24" y1="28" x2="18" y2="30" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="22" y1="40" x2="16" y2="40" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="22" y1="52" x2="16" y2="52" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="24" y1="64" x2="18" y2="62" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="72" y1="16" x2="78" y2="20" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="76" y1="28" x2="82" y2="30" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="78" y1="40" x2="84" y2="40" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="78" y1="52" x2="84" y2="52" stroke="#BA0C2F" stroke-width="2"/>
        <line x1="76" y1="64" x2="82" y2="62" stroke="#BA0C2F" stroke-width="2"/>
      </svg>
      <svg class="absolute bottom-8 left-8 w-20 h-20 opacity-[0.05]" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="46" stroke="white" stroke-width="2"/>
        <path d="M30 10 C15 28, 15 72, 30 90" stroke="#C89B3C" stroke-width="3" stroke-linecap="round"/>
        <path d="M70 10 C85 28, 85 72, 70 90" stroke="#C89B3C" stroke-width="3" stroke-linecap="round"/>
      </svg>

      <!-- Diamond grid texture -->
      <div class="absolute inset-0 diamond-bg-light opacity-30" />
    </div>

    <!-- Card -->
    <div class="relative w-full max-w-sm animate-slide-up">

      <!-- Logo + Branding -->
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center mb-4 animate-float">
          <div class="relative">
            <div class="absolute inset-0 rounded-full blur-xl bg-mlb-gold/30 scale-150" />
            <div class="relative bg-white rounded-full p-2 shadow-[0_0_24px_rgba(200,155,60,0.5)]">
              <BrandLogo :size="52" class="text-mlb-navy-deep" />
            </div>
          </div>
        </div>

        <h1
          class="text-white text-3xl font-black tracking-wide leading-none mb-1"
          style="font-family: var(--font-display); letter-spacing: 0.04em"
        >
          SOFTBALL STATS CLUB
        </h1>
        <p class="text-mlb-gold text-xs font-semibold tracking-[0.2em]" style="font-family: var(--font-display)">YOUR ANALYTICS HUB</p>
      </div>

      <!-- Sign-in card -->
      <div class="login-card">
        <!-- Card header -->
        <div class="text-center mb-6">
          <h2 class="text-white text-lg font-bold" style="font-family: var(--font-display)">WELCOME BACK</h2>
          <p class="text-white/50 text-sm mt-1">Sign in to access your stats</p>
        </div>

        <!-- Error message -->
        <div
          v-if="error"
          class="flex items-center gap-2.5 bg-mlb-red/20 border border-mlb-red/40 text-red-200 text-sm rounded-xl px-4 py-3 mb-5"
        >
          <AlertCircle class="w-4 h-4 shrink-0 text-mlb-red" />
          {{ error }}
        </div>

        <!-- Google button -->
        <button
          @click="handleGoogleLogin"
          :disabled="loading"
          class="google-btn"
        >
          <span v-if="!loading" class="flex items-center justify-center gap-3">
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </span>
          <span v-else class="flex items-center gap-2.5">
            <span class="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            Redirecting to Google…
          </span>
        </button>

        <!-- Divider -->
        <div class="flex items-center gap-3 my-5">
          <div class="flex-1 h-px bg-white/10" />
          <span class="text-white/30 text-xs font-medium" style="font-family: var(--font-display)">INVITE ONLY</span>
          <div class="flex-1 h-px bg-white/10" />
        </div>

        <!-- Info note -->
        <div class="flex items-start gap-2.5 rounded-xl bg-mlb-navy/50 border border-mlb-navy-light/40 px-4 py-3">
          <Info class="w-4 h-4 shrink-0 text-mlb-gold mt-0.5" />
          <p class="text-white/50 text-xs leading-relaxed">
            Access is invite-only. Contact your coach or administrator to receive an invite link.
          </p>
        </div>
      </div>

      <!-- Footer -->
      <p class="text-center text-white/20 text-[11px] mt-6">
        &copy; {{ year }} Softball Stats Club
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { AlertCircle, Info } from 'lucide-vue-next'
import BrandLogo from '@/components/common/BrandLogo.vue'

const loading = ref(false)
const error = ref('')
const year = new Date().getFullYear()

const handleGoogleLogin = async () => {
  if (loading.value) return
  loading.value = true
  error.value = ''
  try {
    const res = await fetch('/api/auth/google')
    const data = await res.json()
    if (data.auth_url) {
      window.location.href = data.auth_url
    } else {
      error.value = 'Failed to initiate Google sign-in'
      loading.value = false
    }
  } catch {
    error.value = 'Could not connect. Please try again.'
    loading.value = false
  }
}
</script>

<style scoped>
.login-bg {
  background: linear-gradient(160deg, #000d1f 0%, #001a3a 35%, #002D62 65%, #001428 100%);
}

.login-card {
  border-radius: 1rem;
  border-width: 1px;
  border-style: solid;
  padding: 1.75rem;
  background: rgba(0, 26, 58, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 1px rgba(200, 155, 60, 0.1), 0 24px 60px rgba(0, 0, 0, 0.5);
}

.google-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: white;
  color: #374151;
  font-weight: 600;
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  font-size: 0.9375rem;
  cursor: pointer;
}

.google-btn:hover {
  background: #f9fafb;
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
}

.google-btn:active {
  background: #f3f4f6;
  transform: scale(0.99);
}

.google-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
